import React from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import {
  H3,
  Page,
  NamedLink,
  LayoutSideNavigation,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import ProfileSettingsForm from './ProfileSettingsForm/ProfileSettingsForm';

import { updateProfile, uploadImage } from './ProfileSettingsPage.duck';
import css from './ProfileSettingsPage.module.css';
import {
  initialValuesForUserFields,
  pickUserFieldsData,
} from '../../util/userHelpers';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { useHistory } from 'react-router-dom';

const onImageUploadHandler = (values, fn) => {
  const { id, imageId, file } = values;
  if (file) {
    fn({ id, imageId, file });
  }
};

export const ProfileSettingsPageComponent = props => {
  const config = useConfiguration();
  const {
    currentUser,
    image,
    onImageUpload,
    onUpdateProfile,
    scrollingDisabled,
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
    intl,
  } = props;
  const routesConfiguration = useRouteConfiguration();
  const history = useHistory();
  const { userFields, userTypes = [] } = config.user;
  const { subscription } = currentUser?.attributes?.profile?.publicData || {};

  const handleSubmit = (values, userType) => {
    const {
      firstName,
      lastName,
      displayName: displayNameInput,
      householdMembers,
      bio: rawBio,
      communityReason,
      hometown,
      familyName,
      ...rest
    } = values;
    const displayName = (displayNameInput || lastName || '').trim();
    const displayNameMaybe = displayName
      ? { displayName }
      : { displayName: null };

    // Ensure that the optional bio is a string
    const bio = rawBio || '';

    const profile = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ...displayNameMaybe,
      bio,
      publicData: {
        communityReason,
        hometown,
        familyName,
        householdMembers: householdMembers || [],
        ...pickUserFieldsData(rest, 'public', userType, userFields),
      },
      protectedData: {
        ...pickUserFieldsData(rest, 'protected', userType, userFields),
      },
      privateData: {
        ...pickUserFieldsData(rest, 'private', userType, userFields),
      },
    };
    const uploadedImage = props.image;

    // Update profileImage only if file system has been accessed
    const updatedValues =
      uploadedImage && uploadedImage.imageId && uploadedImage.file
        ? { ...profile, profileImageId: uploadedImage.imageId }
        : profile;

    onUpdateProfile(updatedValues)
      .then(() => {
        // Give the user a moment to see the save confirmation (checkmark)
        // on the button before navigating away.
        window.setTimeout(() => {
          if (subscription === false) {
            history.push(
              createResourceLocatorString(
                'SubscriptionPage',
                routesConfiguration,
                {},
              )
            );
          } else {
            history.push(
              createResourceLocatorString(
                'ProfilePage',
                routesConfiguration,
                { id: user.id.uuid },
              )
            );
          }
        }, 1000);
      })
      .catch(() => {
        // updateProfileError is already surfaced via redux state;
        // stay on the page so the user can see the error and retry.
      });
  };

  const user = ensureCurrentUser(currentUser);
  const {
    firstName,
    lastName,
    displayName,
    bio,
    publicData,
    protectedData,
    privateData,
  } = user?.attributes.profile;
  const { userType, communityReason, hometown, familyName } = publicData || {};
  const profileImageId = user.profileImage ? user.profileImage.id : null;
  const profileImage = image || { imageId: profileImageId };
  const userTypeConfig = userTypes.find(config => config.userType === userType);
  const isDisplayNameIncluded =
    userTypeConfig?.defaultUserFields?.displayName !== false;
  // ProfileSettingsForm decides if it's allowed to show the input field.
  const displayNameMaybe =
    isDisplayNameIncluded ? { displayName: displayName || familyName || lastName || '' } : {};

  // Initialize familyName with lastName if not set
  const familyNameValue = familyName || lastName || '';

  const profileSettingsForm = user.id ? (
    <ProfileSettingsForm
      className={css.form}
      currentUser={currentUser}
      initialValues={{
        firstName,
        lastName,
        ...displayNameMaybe,
        bio,
        profileImage: user.profileImage,
        communityReason,
        hometown,
        familyName: familyNameValue,
        householdMembers: publicData?.householdMembers || [],
        ...initialValuesForUserFields(
          publicData,
          'public',
          userType,
          userFields
        ),
        ...initialValuesForUserFields(
          protectedData,
          'protected',
          userType,
          userFields
        ),
        ...initialValuesForUserFields(
          privateData,
          'private',
          userType,
          userFields
        ),
      }}
      profileImage={profileImage}
      onImageUpload={e => onImageUploadHandler(e, onImageUpload)}
      uploadInProgress={uploadInProgress}
      updateInProgress={updateInProgress}
      uploadImageError={uploadImageError}
      updateProfileError={updateProfileError}
      onSubmit={values => handleSubmit(values, userType)}
      marketplaceName={config.marketplaceName}
      userFields={userFields}
      userTypeConfig={userTypeConfig}
    />
  ) : null;

  const title = intl.formatMessage({ id: 'ProfileSettingsPage.title' });

  return (
    <Page
      className={css.root}
      title={title}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSideNavigation
        topbar={
          <>
            <TopbarContainer
              desktopClassName={css.desktopTopbar}
              mobileClassName={css.mobileTopbar}
            />
            {/* <UserNav currentPage="ProfileSettingsPage" /> */}
          </>
        }
        sideNav={null}
        useAccountSettingsNav
        currentPage="ProfileSettingsPage"
        footer={<FooterContainer />}
      >
        <div className={css.content}>
          <div className={css.headingContainer}>
            <H3 as="h1" className={css.heading}>
              <FormattedMessage id="ProfileSettingsPage.heading" />
            </H3>
            {user.id ? (
              <NamedLink
                className={css.profileLink}
                name="ProfilePage"
                params={{ id: user.id.uuid }}
              >
                <FormattedMessage id="ProfileSettingsPage.viewProfileLink" />
              </NamedLink>
            ) : null}
          </div>
          {profileSettingsForm}
        </div>
      </LayoutSideNavigation>
    </Page>
  );
};

ProfileSettingsPageComponent.defaultProps = {
  currentUser: null,
  uploadImageError: null,
  updateProfileError: null,
  image: null,
  config: null,
};

ProfileSettingsPageComponent.propTypes = {
  currentUser: propTypes.currentUser,
  image: shape({
    id: string,
    imageId: propTypes.uuid,
    file: object,
    uploadedImage: propTypes.image,
  }),
  onImageUpload: func.isRequired,
  onUpdateProfile: func.isRequired,
  scrollingDisabled: bool.isRequired,
  updateInProgress: bool.isRequired,
  updateProfileError: propTypes.error,
  uploadImageError: propTypes.error,
  uploadInProgress: bool.isRequired,

  // from useConfiguration()
  config: object,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    image,
    uploadImageError,
    uploadInProgress,
    updateInProgress,
    updateProfileError,
  } = state.ProfileSettingsPage;
  return {
    currentUser,
    image,
    scrollingDisabled: isScrollingDisabled(state),
    updateInProgress,
    updateProfileError,
    uploadImageError,
    uploadInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onImageUpload: data => dispatch(uploadImage(data)),
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const ProfileSettingsPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ProfileSettingsPageComponent);

export default ProfileSettingsPage;
